import React, { useEffect, useRef, useState } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { COLORS } from '../../styles/tokens';
import { pinAddress } from '../../utils/pinAddress';

const OSLO = { lat: 59.9139, lng: 10.7522 };
const GEOCODE_TIMEOUT_MS = 8000;

// Bunnark med kart der bestiller setter en pin (trykk eller dra). Brukes når stedet ikke har en nøyaktig adresse.
// Må bare vises når Google Maps er lastet (AddressInput sørger for det).
// initial: { lat, lng } eller null. onConfirm får { street, city, lat, lng, pinned: true }.
export default function MapPinPicker({ initial, onConfirm, onClose }) {
  const hasInitial = initial?.lat != null && initial?.lng != null;
  const [pos, setPos] = useState(hasInitial ? { lat: initial.lat, lng: initial.lng } : null);
  const [center, setCenter] = useState(hasInitial ? { lat: initial.lat, lng: initial.lng } : OSLO);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const unmounted = useRef(false);

  useEffect(() => {
    unmounted.current = false;
    return () => { unmounted.current = true; clearTimeout(timer.current); };
  }, []);

  // Uten startposisjon: sentrer på enhetens posisjon hvis den er tilgjengelig
  useEffect(() => {
    if (hasInitial || !navigator.geolocation) return undefined;
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      p => { if (!cancelled) setCenter({ lat: p.coords.latitude, lng: p.coords.longitude }); },
      () => {},
      { timeout: 5000 },
    );
    return () => { cancelled = true; };
  }, [hasInitial]);

  const pick = e => setPos({ lat: e.latLng.lat(), lng: e.latLng.lng() });

  function confirm() {
    if (!pos || busy) return;
    setBusy(true);
    let done = false;
    const finish = (results, status) => {
      if (done || unmounted.current) return;
      done = true;
      clearTimeout(timer.current);
      onConfirm(pinAddress(pos.lat, pos.lng, results, status));
    };
    try {
      const geocoder = window.google?.maps?.Geocoder ? new window.google.maps.Geocoder() : null;
      if (!geocoder) { finish(null, null); return; }
      // Uteblir svaret, bekreft med koordinater så dialogen ikke låser seg
      timer.current = setTimeout(() => finish(null, null), GEOCODE_TIMEOUT_MS);
      geocoder.geocode({ location: pos }, finish);
    } catch {
      finish(null, null);
    }
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="Sett pin på kart" style={styles.overlay}>
      <div style={styles.sheet}>
        <h2 style={styles.title}>Sett pin på kart</h2>
        <p style={styles.hint}>Trykk på kartet eller dra pinnen til stedet.</p>
        <GoogleMap
          mapContainerStyle={styles.map}
          center={center}
          zoom={hasInitial ? 16 : 13}
          onClick={pick}
          options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
        >
          {pos && <Marker position={pos} draggable onDragEnd={pick} />}
        </GoogleMap>
        <button type="button" style={{ ...styles.primary, opacity: pos && !busy ? 1 : 0.5 }} disabled={!pos || busy} onClick={confirm}>
          {busy ? 'Henter adresse…' : 'Bruk dette stedet'}
        </button>
        <button type="button" style={styles.ghost} onClick={onClose}>Avbryt</button>
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 },
  sheet:   { width: '100%', maxWidth: 560, background: COLORS.white, borderRadius: '20px 20px 0 0', padding: 16, boxSizing: 'border-box' },
  title:   { margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: COLORS.textDark },
  hint:    { margin: '0 0 10px', fontSize: 13.5, color: COLORS.textMid },
  map:     { width: '100%', height: 320, borderRadius: 14 },
  primary: { marginTop: 12, width: '100%', height: 48, borderRadius: 14, border: 'none', background: COLORS.brandPanel, color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  ghost:   { marginTop: 8, width: '100%', height: 44, borderRadius: 14, border: `1px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textDark, fontSize: 14.5, fontWeight: 600, cursor: 'pointer' },
};
